import React from 'react';
import {
  Box,
  Flex,
  Text,
  Select,
  Button,
  Checkbox,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Textarea,
  IconButton,
} from '@chakra-ui/react';
import { FiUpload, FiCheck, FiX, FiTrash2 } from 'react-icons/fi';
import dayjs from 'dayjs';
import xhr from 'xhr';

const STATUS_LABEL = {
  approved: 'Aprobado',
  pending: 'Pendiente',
  denied: 'Rechazado',
};

const STATUS_COLOR = {
  approved: 'green',
  pending: 'orange',
  denied: 'red',
};

export default function SchoolRequisitosTab({ studentId, schoolId, programTypes }) {
  const toast = useToast();
  const fileInputRef = React.useRef(null);

  const programOptions = React.useMemo(() => {
    if (!programTypes) return [];
    return programTypes.map((pt) => ({ id: pt.id, name: pt.name }));
  }, [programTypes]);

  const [selectedProgramType, setSelectedProgramType] = React.useState(
    programOptions[0]?.id ?? null
  );

  React.useEffect(() => {
    if (!selectedProgramType && programOptions[0]?.id) {
      setSelectedProgramType(programOptions[0].id);
    }
  }, [programOptions, selectedProgramType]);

  const [catalog, setCatalog] = React.useState([]);
  const [sittings, setSittings] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [file, setFile] = React.useState(null);
  const [loadingList, setLoadingList] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [denyModal, setDenyModal] = React.useState({ open: false, sitting: null, comment: '' });

  const loadData = React.useCallback(async () => {
    if (!selectedProgramType || !studentId) return;
    setLoadingList(true);
    try {
      const [catRes, sitRes] = await Promise.all([
        xhr(
          `/requirements?filters[program_type][id][$eq]=${selectedProgramType}&pagination[limit]=200`
        ),
        xhr(
          `/requirement-sittings?filters[student][id][$eq]=${studentId}&filters[program_type][id][$eq]=${selectedProgramType}&populate=*&pagination[limit]=200`
        ),
      ]);
      setCatalog(catRes.data?.data || []);
      setSittings(sitRes.data?.data || []);
    } catch (err) {
      console.error(err);
      toast({ status: 'error', title: 'No se pudo cargar los requisitos', duration: 4000 });
    } finally {
      setLoadingList(false);
    }
  }, [selectedProgramType, studentId, toast]);

  React.useEffect(() => {
    setSelectedIds([]);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    loadData();
  }, [loadData]);

  const coveredIds = React.useMemo(() => {
    const set = new Set();
    sittings.forEach((s) => {
      if (s.requirement?.id) set.add(s.requirement.id);
      (s.requirements_many || []).forEach((r) => r?.id && set.add(r.id));
    });
    return set;
  }, [sittings]);

  const pendingCatalog = React.useMemo(
    () => catalog.filter((r) => !coveredIds.has(r.id)),
    [catalog, coveredIds]
  );

  const toggleId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleUpload = async () => {
    if (selectedIds.length === 0) {
      toast({ status: 'warning', title: 'Selecciona al menos un requisito', duration: 3000 });
      return;
    }
    if (!file) {
      toast({ status: 'warning', title: 'Sube un archivo PDF', duration: 3000 });
      return;
    }

    setUploading(true);
    try {
      const createRes = await xhr.post('/requirement-sittings', {
        data: {
          student: studentId,
          program_type: selectedProgramType,
          requirements_many: selectedIds,
          status: 'pending',
          method: 'scanned',
          school: schoolId,
        },
      });

      const newId = createRes.data?.data?.id;
      if (!newId) throw new Error('Respuesta invalida al crear el requisito');

      const formData = new FormData();
      formData.append('files', file);
      formData.append('ref', 'api::requirement-sitting.requirement-sitting');
      formData.append('refId', newId);
      formData.append('field', 'file');

      await xhr.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSelectedIds([]);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast({ status: 'success', title: 'Requisito cargado', duration: 3000 });
      await loadData();
    } catch (err) {
      console.error(err);
      toast({
        status: 'error',
        title: 'No se pudo cargar el requisito',
        description: err.response?.data?.error?.message || err.message,
        duration: 4000,
      });
    } finally {
      setUploading(false);
    }
  };

  const updateStatus = async (sitting, newStatus, comment) => {
    try {
      const payload = { status: newStatus };
      if (typeof comment === 'string') payload.comment = comment;
      await xhr.put(`/requirement-sittings/${sitting.id}`, { data: payload });
      toast({
        status: 'success',
        title: newStatus === 'approved' ? 'Requisito aprobado' : 'Requisito rechazado',
        duration: 3000,
      });
      await loadData();
    } catch (err) {
      console.error(err);
      toast({ status: 'error', title: 'No se pudo actualizar', duration: 4000 });
    }
  };

  const handleDelete = async (sitting) => {
    if (!window.confirm('Eliminar este requisito?')) return;
    try {
      await xhr.delete(`/requirement-sittings/${sitting.id}`);
      if (sitting.file?.id) {
        try {
          await xhr.delete(`/upload/files/${sitting.file.id}`);
        } catch (_) { }
      }
      toast({ status: 'success', title: 'Requisito eliminado', duration: 3000 });
      await loadData();
    } catch (err) {
      console.error(err);
      toast({ status: 'error', title: 'No se pudo eliminar', duration: 4000 });
    }
  };

  const openDeny = (sitting) => {
    setDenyModal({ open: true, sitting, comment: sitting.comment || '' });
  };

  const closeDeny = () => setDenyModal({ open: false, sitting: null, comment: '' });

  const confirmDeny = async () => {
    const { sitting, comment } = denyModal;
    if (!sitting) return;
    await updateStatus(sitting, 'denied', comment.trim());
    closeDeny();
  };

  if (!programTypes || programTypes.length === 0) {
    return (
      <Box p={4} color="gray.500" fontSize="sm">
        El estudiante no tiene programas matriculados. Asigna un programa para gestionar sus requisitos.
      </Box>
    );
  }

  return (
    <Box>
      {programOptions.length > 1 && (
        <Flex mb={4} align="center" gap={3}>
          <Text fontSize="sm" fontWeight="500">Programa:</Text>
          <Select
            size="sm"
            width="auto"
            value={selectedProgramType || ''}
            onChange={(e) => setSelectedProgramType(Number(e.target.value))}
          >
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </Flex>
      )}

      {loadingList ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : (
        <Flex direction={{ base: 'column', lg: 'row' }} gap={5} align="flex-start">
          {/* Left: upload */}
          <Box
            flex="1"
            bg="gray.50"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            p={4}
          >
            <Text fontWeight="600" mb={3}>Cargar requisitos</Text>
            {pendingCatalog.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                Este estudiante ya tiene todos los requisitos registrados.
              </Text>
            ) : (
              <>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Marca los requisitos que estan contenidos en el PDF que subes.
                </Text>
                <Box maxH="240px" overflowY="auto" mb={3}>
                  {pendingCatalog.map((r) => (
                    <Flex key={r.id} mb={2} gap={2} align="flex-start">
                      <Checkbox
                        colorScheme="purple"
                        isChecked={selectedIds.includes(r.id)}
                        onChange={() => toggleId(r.id)}
                        mt="2px"
                      />
                      <Box
                        fontSize="sm"
                        onClick={() => toggleId(r.id)}
                        cursor="pointer"
                        dangerouslySetInnerHTML={{ __html: r.name || r.title || '' }}
                      />
                    </Flex>
                  ))}
                </Box>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  size="sm"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  mb={3}
                  p={1}
                />
                <Button
                  leftIcon={<FiUpload />}
                  colorScheme="purple"
                  bg="#574feb"
                  size="sm"
                  isLoading={uploading}
                  onClick={handleUpload}
                >
                  Subir
                </Button>
              </>
            )}
          </Box>

          {/* Right: sent */}
          <Box
            flex="1.2"
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
          >
            <Box p={4} pb={2}>
              <Text fontWeight="600">Documentos enviados</Text>
            </Box>
            {sittings.length === 0 ? (
              <Box p={4} color="gray.500" fontSize="sm">
                Aun no hay requisitos registrados para este programa.
              </Box>
            ) : (
              <TableContainer>
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Requisitos</Th>
                      <Th>Estado</Th>
                      <Th>Fecha</Th>
                      <Th>Archivo</Th>
                      <Th textAlign="right">Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sittings.map((s) => (
                      <Tr key={s.id}>
                        <Td>
                          {s.requirement ? (
                            <Box
                              fontSize="sm"
                              dangerouslySetInnerHTML={{ __html: s.requirement.name || '' }}
                            />
                          ) : (
                            <Box>
                              {(s.requirements_many || []).map((r) => (
                                <Box
                                  key={r.id}
                                  fontSize="sm"
                                  dangerouslySetInnerHTML={{ __html: r.name || '' }}
                                />
                              ))}
                            </Box>
                          )}
                          {s.comment && s.status === 'denied' && (
                            <Text fontSize="xs" color="red.500" mt={1}>
                              Motivo: {s.comment}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Badge colorScheme={STATUS_COLOR[s.status] || 'gray'} fontSize="xs">
                            {STATUS_LABEL[s.status] || s.status}
                          </Badge>
                        </Td>
                        <Td fontSize="xs" color="gray.600">
                          {dayjs(s.createdAt).format('DD/MM/YY')}
                        </Td>
                        <Td>
                          {s.file ? (
                            <a href={s.file.url} target="_blank" rel="noreferrer" style={{ color: '#574feb', fontSize: 12 }}>
                              Ver
                            </a>
                          ) : (
                            <Text fontSize="xs" color="gray.400">-</Text>
                          )}
                        </Td>
                        <Td>
                          <Flex justify="flex-end" gap={1}>
                            {s.status !== 'approved' && (
                              <IconButton
                                aria-label="Aprobar"
                                size="xs"
                                colorScheme="green"
                                variant="ghost"
                                icon={<FiCheck />}
                                onClick={() => updateStatus(s, 'approved', '')}
                              />
                            )}
                            {s.status !== 'denied' && (
                              <IconButton
                                aria-label="Rechazar"
                                size="xs"
                                colorScheme="orange"
                                variant="ghost"
                                icon={<FiX />}
                                onClick={() => openDeny(s)}
                              />
                            )}
                            <IconButton
                              aria-label="Eliminar"
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              icon={<FiTrash2 />}
                              onClick={() => handleDelete(s)}
                            />
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Flex>
      )}

      <Modal isOpen={denyModal.open} onClose={closeDeny} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rechazar requisito</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={2}>Motivo del rechazo (opcional):</Text>
            <Textarea
              rows={4}
              value={denyModal.comment}
              onChange={(e) => setDenyModal((s) => ({ ...s, comment: e.target.value }))}
              placeholder="Ej: el documento esta ilegible, falta la firma..."
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={closeDeny}>Cancelar</Button>
            <Button colorScheme="orange" onClick={confirmDeny}>Rechazar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
